import { useRef, useState } from "react";
import { useLocalization } from "../../hooks/use-localization/useLocalization";
import { Page } from "../../components/page/page";
import { useApp } from "../../hooks/use-app/useApp";
import { StatusMessage } from "../../components/status-message/statusMessage";
import { useStore } from "../../hooks/use-store/useStore";
import { PasswordInput } from "../../components/input/passwordInput";
import { Providers } from "../../components/providers/providers";
import { Form } from "../../components/form/form";

export const Register: React.FC = () => {
  const { localize } = useLocalization();
  const [registerOk, setRegisterOk] = useState<boolean>(false);
  const store = useStore();
  const app = useApp();
  const alternativeProviders = app.providers.filter((p) => p.slug !== "email");
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const isInvite = store.query.invite && !!store.query.email;
  const prefilledEmail = store.query.email;

  function validatePassword() {
    const password = passwordRef.current;
    const confirmPassword = confirmPasswordRef.current;

    if (!password || !confirmPassword) {
      return;
    }

    if (password.value !== confirmPassword.value) {
      confirmPassword.setCustomValidity(localize("register:passwordMismatch"));
    } else {
      confirmPassword.setCustomValidity("");
    }
  }

  if (app.inviteOnly && !isInvite) {
    return (
      <Page title={localize("register:title")} showLoginLink>
        <StatusMessage>{localize("register:inviteOnly")}</StatusMessage>
      </Page>
    );
  }

  if (registerOk) {
    if (app.verify) {
      return (
        <Page title={localize("register:emailVerificationRequiredTitle")}>
          <StatusMessage>
            {localize("register:emailVerificationRequiredText")}
          </StatusMessage>
        </Page>
      );
    }

    return (
      <Page title={localize("register:successTitle")}>
        <StatusMessage type="success">
          {localize("register:successText")}
        </StatusMessage>
        <a className="button" href={`/${app.name}/api/autologin`}>
          {localize("complete")}
        </a>
      </Page>
    );
  }

  return (
    <Page title={localize("register:title")} showLoginLink>
      <>
        {app.inviteOnly && prefilledEmail && (
          <>
            <div className="card">
              <h2 className="h3">{prefilledEmail}</h2>
            </div>
            <StatusMessage>
              {localize(
                "register:inviteOnlyDescription",
                app.label || app.name,
              )}
            </StatusMessage>
          </>
        )}
        <Form
          action="/register"
          method="POST"
          actionLabel={localize("register:action")}
          onSuccess={() => setRegisterOk(true)}
          errorMap={{
            userAlreadyExists: localize("register:userAlreadyExists"),
            missingEmailOrPassword: localize("register:missingEmailOrPassword"),
          }}
        >
          <label>
            {localize("firstName")}
            <input
              type="text"
              name="firstName"
              placeholder="Ditt fornavn"
              autoComplete="given-name"
              required
            />
          </label>
          <label>
            {localize("lastName")}
            <input
              type="text"
              name="lastName"
              placeholder="Ditt etternavn"
              autoComplete="family-name"
              required
            />
          </label>
          <label>
            {localize("email")}
            <input
              type="email"
              name="email"
              placeholder="Skriv inn din e-postadresse"
              autoComplete="email"
              defaultValue={prefilledEmail}
              readOnly={isInvite && !!prefilledEmail}
              required
            />
          </label>
          <PasswordInput
            label={localize("password")}
            ref={passwordRef}
            name="password"
            placeholder="Skriv inn ditt passord"
            autoComplete="new-password"
            required
            onChange={validatePassword}
          />
          <PasswordInput
            label={localize("register:confirmPassword")}
            ref={confirmPasswordRef}
            name="confirmPassword"
            placeholder="Bekreft passordet ditt"
            autoComplete="new-password"
            required
            onChange={validatePassword}
          />
          <label className="b1">
            <input type="checkbox" name="agreedToTerms" required />
            {localize("consent:basic")}
          </label>
        </Form>
        <Providers hasEmailProvider providers={alternativeProviders} />
      </>
    </Page>
  );
};
